import type { KeyboardEventHandler } from 'react';

import type { StudyAnswerStatus } from '../lib/study-feedback.ts';
import { CardMarkdown } from './CardMarkdown.tsx';
import { cn } from '../lib/utils.ts';

type StudyChoiceOptionProps = {
  disabled: boolean;
  index: number;
  onClick: () => void;
  onKeyDown: KeyboardEventHandler<HTMLButtonElement>;
  option: { id: string; text: string };
  selected: boolean;
  status: StudyAnswerStatus | undefined;
};

export function StudyChoiceOption({
  disabled,
  index,
  onClick,
  onKeyDown,
  option,
  selected,
  status,
}: StudyChoiceOptionProps) {
  const teacherMark =
    status === 'correct' && selected
      ? '✓'
      : status === 'incorrect'
        ? selected
          ? '✕'
          : '✓'
        : null;
  const selectedMarkWidth = status === 'correct' ? 'w-7' : 'w-6';

  return (
    <button
      className="flex w-full items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 text-start [--choice-option-surface:var(--background)]"
      disabled={disabled}
      onClick={onClick}
      onKeyDown={onKeyDown}
      type="button"
    >
      <span className="inline-grid min-w-6 size-6 shrink-0 place-items-center font-mono text-2xl leading-none text-muted-foreground">
        {index + 1}
      </span>
      <span>
        <CardMarkdown links="text" source={option.text} />
      </span>
      {selected ? (
        <span
          className={cn('relative ms-auto h-6 shrink-0', selectedMarkWidth)}
        >
          <span className="absolute inset-y-0 start-0 inline-grid size-6 place-items-center text-2xl leading-none text-foreground">
            ✓
          </span>
          {teacherMark ? (
            <span
              className={cn(
                'absolute inset-y-0 inline-grid size-6 place-items-center text-2xl leading-none [-webkit-text-stroke:1px_var(--choice-option-surface)] [paint-order:stroke_fill]',
                status === 'correct' ? 'start-1' : 'start-0',
                status === 'correct' ? 'text-success' : 'text-destructive',
              )}
            >
              {teacherMark}
            </span>
          ) : null}
        </span>
      ) : teacherMark ? (
        <span
          className={cn(
            'ms-auto inline-grid size-6 shrink-0 place-items-center text-2xl leading-none',
            status === 'correct' ? 'text-success' : 'text-destructive',
          )}
        >
          {teacherMark}
        </span>
      ) : null}
    </button>
  );
}
